import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  PendingActionItem,
  ActionType,
  ActionRequired,
  Priority,
  PendingActionDetails,
} from "../types/pending.types";
import { isTimesheetApprovalDetails, isItemRequestDetails, isOrderDetails, isLeaveApprovalDetails, isTaskDeadlineDetails, isDataChangeApprovalDetails, isProjectRemovalRequestDetails } from "../types/pending.types";
import { useGetPendingActionsQuery } from "../api/pendingActions";

export interface NormalizedPendingItem {
  id: string | number;
  type:
    | "leave"
    | "amendment"
    | "termination"
    | "hiring"
    | "order"
    | "signing_leave"
    | "signing_resignation"
    | "signing_amendment"
    | "signing_contract"
    | "signing_hiring"
    | "signing_contractor"
    | "signing_legal"
    | "signing_act"
    | "timesheet"
    | "unit_request"
    | "gph_payment"
    | "task_deadline"
    | "fsm_submission"
    | "data_change"
    | "personal_sheet_fill"
    | "personal_sheet_sign"
    | "advance"
    | "business_trip"
    | "project_removal";
  entityId: number | null;
  title: string;
  workerName: string;
  createdAt: string;
  priority: Priority;
  actionRequired: ActionRequired;
  details: PendingActionDetails;
  originalType: ActionType;
  subject_ru: string;
  subject_kk: string;
  subject_en: string;
}

function normalizePendingItem(item: PendingActionItem, locale: string): NormalizedPendingItem {
  const typeMap: Record<ActionType, NormalizedPendingItem["type"]> = {
    leave_application: "leave",
    amendment: "amendment",
    termination_order: "termination",
    hiring_order: "hiring",
    vacation_order: "order",
    medical_order: "order",
    amendment_order: "order",
    annual_leave: "signing_leave",
    unpaid_leave: "signing_leave",
    medical_leave: "signing_leave",
    resignation_letter: "signing_resignation",
    amendment_application: "signing_amendment",
    amendment_agreement: "signing_amendment",
    contract_draft: "signing_contract",
    job_application: "signing_hiring",
    service_agreement_individual: "signing_contractor",
    service_agreement_msb: "signing_contractor",
    vehicle_rental: "signing_legal",
    premises_lease: "signing_legal",
    goods_supply: "signing_legal",
    completion_act: "signing_act",
    vehicle_handover: "signing_act",
    premises_handover: "signing_act",
    timesheet_draft: "timesheet",
    unit_request: "unit_request",
    unit_request_fulfill: "unit_request",
    gph_payment: "gph_payment",
    task_deadline_approaching: "task_deadline",
    fsm_submission: "fsm_submission",
    data_change_request: "data_change",
    personal_sheet_completion: "personal_sheet_fill",
    personal_sheet_signature: "personal_sheet_sign",
    advance: "advance",
    business_trip: "business_trip",
    business_trip_order: "order",
    project_removal_request: "project_removal",
  };

  let entityId: number | null = Number(item.id);

  if (item.action_required === "revise_application") {
    entityId = Number(item.id);
  } else if (item.action_required === "sign_document") {
    entityId = Number(item.id);
  } else if (item.action_required === "acknowledge_order") {
    entityId = Number(item.id);
  } else if (item.type === "termination_order") {
    entityId = isLeaveApprovalDetails(item.details) && item.details.leave_id ? item.details.leave_id : Number(item.id);
  } else if (item.type === "resignation_letter") {
    entityId = isLeaveApprovalDetails(item.details) && item.details.leave_id ? item.details.leave_id : Number(item.id);
  } else if (item.type === "vacation_order" || item.type === "medical_order") {
    entityId = Number(item.id);
  } else if (item.type === "amendment_order") {
    entityId = isOrderDetails(item.details) && item.details.amendment_id ? item.details.amendment_id : Number(item.id);
  } else if (item.type === "amendment") {
    entityId = Number(item.id);
  } else if (item.action_required === "approve_draft") {
    entityId = Number(item.id);
  } else if (item.action_required === "approve_timesheet") {
    entityId = isTimesheetApprovalDetails(item.details) ? item.details.timesheet_id : Number(item.id);
  } else if (item.type === "unit_request" || item.type === "unit_request_fulfill") {
    entityId = isItemRequestDetails(item.details) ? item.details.request_id : Number(item.id);
  } else if (item.action_required === "review_gph_payment") {
    entityId = Number(item.id);
  } else if (item.type === "task_deadline_approaching") {
    entityId = isTaskDeadlineDetails(item.details) ? item.details.task_id : Number(item.id);
  } else if (item.type === "fsm_submission") {
    entityId = Number(item.id);
  } else if (item.type === "data_change_request") {
    entityId = isDataChangeApprovalDetails(item.details) ? item.details.request_id : Number(item.id);
  } else if (item.action_required === "approve_project_removal") {
    entityId = isProjectRemovalRequestDetails(item.details) ? item.details.project_id : Number(item.id);
  }

  const title = locale === "kk" ? item.subject_kk : locale === "en" ? item.subject_en : item.subject_ru;

  return {
    id: item.id,
    type: typeMap[item.type],
    entityId,
    title,
    workerName: item.worker_name,
    createdAt: item.created_at,
    priority: item.priority,
    actionRequired: item.action_required,
    details: item.details,
    originalType: item.type,
    subject_ru: item.subject_ru,
    subject_kk: item.subject_kk,
    subject_en: item.subject_en,
  };
}

export function usePendingActions(options?: { pauseRefetch?: boolean }) {
  const { i18n } = useTranslation();
  const { data, isLoading, error, refetch } = useGetPendingActionsQuery(undefined, {
    pollingInterval: options?.pauseRefetch ? 0 : 60_000,
    refetchOnFocus: !options?.pauseRefetch,
  });
  const summary = data?.summary || {
    total_count: 0,
    by_type: {},
    by_action: {},
  };

  const normalizedItems = useMemo(() => {
    if (!data?.items) return [];
    return data.items.map((item: PendingActionItem) => normalizePendingItem(item, i18n.language));
  }, [data?.items, i18n.language]);

  return {
    items: normalizedItems,
    summary,
    isLoading,
    error,
    refetch,
  };
}
